import { useInView } from "../hooks/useInView";
import {
  Heart,
  Zap,
  Brain,
  Baby,
  Leaf,
  ShieldCheck,
  ClipboardList,
  Stethoscope,
  RefreshCw,
} from "lucide-react";

const SERVICES = [
  {
    icon: <Heart className="w-7 h-7" />,
    title: "Chronic Diseases",
    desc: "Conditions like asthma, arthritis, psoriasis, diabetes, hypertension, and more can be managed with our expert homeopathic care.",
  },
  {
    icon: <Zap className="w-7 h-7" />,
    title: "Acute Conditions",
    desc: "Colds, fevers, digestive problems, and other short-term illnesses are treated safely and swiftly without suppressing symptoms.",
  },
  {
    icon: <Brain className="w-7 h-7" />,
    title: "Mental Health",
    desc: "Anxiety, depression, stress, insomnia, and more. We offer gentle, natural support for mental and emotional well-being.",
  },
  {
    icon: <ShieldCheck className="w-7 h-7" />,
    title: "Women's Health",
    desc: "Menstrual disorders, menopause symptoms, hormonal imbalances, and more, addressed with specialized homeopathic care.",
  },
  {
    icon: <Baby className="w-7 h-7" />,
    title: "Children's Health",
    desc: "Homeopathy is especially suited for children, offering relief from allergies, digestive issues, behavioral problems, and skin conditions without harsh medications.",
  },
  {
    icon: <Leaf className="w-7 h-7" />,
    title: "Allergies & Skin Conditions",
    desc: "Homeopathy is highly effective in managing allergic reactions, eczema, dermatitis, and other skin conditions.",
  },
];

const PROCESS = [
  {
    icon: <ClipboardList className="w-6 h-6" />,
    title: "In-Depth Consultation",
    desc: "We begin with a thorough consultation to understand your symptoms, lifestyle, medical history, and emotional well-being. This ensures that the treatment is specific to your needs.",
  },
  {
    icon: <Stethoscope className="w-6 h-6" />,
    title: "Personalized Treatment",
    desc: "Based on the consultation, we design a customized homeopathic plan that addresses not only your symptoms but also the underlying causes of the illness.",
  },
  {
    icon: <RefreshCw className="w-6 h-6" />,
    title: "Ongoing Support",
    desc: "Healing is a journey, and we are here to support you every step of the way. Regular follow-ups allow us to monitor your progress and adjust your treatment as necessary.",
  },
];

function ServiceCard({ service, index }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div
      ref={ref}
      className="bg-white rounded-2xl p-6 shadow-sm border border-green-50 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 group"
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.5s ease ${index * 0.08}s, transform 0.5s ease ${index * 0.08}s, box-shadow 0.3s, translate 0.3s`,
      }}
    >
      <div className="w-14 h-14 rounded-xl bg-lightbg flex items-center justify-center text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors duration-300">
        {service.icon}
      </div>
      <h3 className="font-display font-semibold text-darktext text-lg mb-2">
        {service.title}
      </h3>
      <p className="text-gray-600 text-sm leading-relaxed">{service.desc}</p>
    </div>
  );
}

function ProcessStep({ step, index, total }) {
  const [ref, inView] = useInView(0.15);
  return (
    <div className="flex flex-col items-center text-center relative flex-1" ref={ref}>
      {/* Connector line (not after last) */}
      {index < total - 1 && (
        <div
          className="hidden md:block absolute top-8 left-1/2 w-full h-0.5 bg-gradient-to-r from-accent to-transparent z-0"
          style={{
            opacity: inView ? 1 : 0,
            transition: `opacity 0.8s ease ${index * 0.2 + 0.3}s`,
          }}
        />
      )}
      <div
        className="relative z-10 w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center shadow-lg mb-4"
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? "scale(1)" : "scale(0.6)",
          transition: `opacity 0.5s ease ${index * 0.2}s, transform 0.5s ease ${index * 0.2}s`,
        }}
      >
        {step.icon}
        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-accent text-white text-xs flex items-center justify-center font-bold">
          {index + 1}
        </span>
      </div>
      <div
        style={{
          opacity: inView ? 1 : 0,
          transform: inView ? "translateY(0)" : "translateY(15px)",
          transition: `opacity 0.5s ease ${index * 0.2 + 0.15}s, transform 0.5s ease ${index * 0.2 + 0.15}s`,
        }}
      >
        <h4 className="font-display font-semibold text-darktext mb-2">{step.title}</h4>
        <p className="text-gray-600 text-sm leading-relaxed max-w-56 mx-auto">{step.desc}</p>
      </div>
    </div>
  );
}

export default function Services() {
  const [headRef, headInView] = useInView(0.2);

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div
          ref={headRef}
          className="text-center mb-14"
          style={{
            opacity: headInView ? 1 : 0,
            transform: headInView ? "translateY(0)" : "translateY(25px)",
            transition: "opacity 0.6s ease, transform 0.6s ease",
          }}
        >
          <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary mb-3">
            What We Treat
          </span>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-darktext mb-4">
            Our Services
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-base leading-relaxed">
            At Silver Line Homeo Care, we successfully treat a wide range of health issues,
            including:
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.title} service={s} index={i} />
          ))}
        </div>

        {/* Process section */}
        <div className="bg-lightbg rounded-3xl p-8 sm:p-12">
          <div
            className="text-center mb-12"
            style={{
              opacity: headInView ? 1 : 0,
              transition: "opacity 0.6s ease 0.3s",
            }}
          >
            <span className="inline-block text-xs font-semibold tracking-widest uppercase text-primary mb-3">
              How It Works
            </span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-darktext">
              Our Process
            </h2>
          </div>
          <div className="flex flex-col md:flex-row gap-10 md:gap-4">
            {PROCESS.map((step, i) => (
              <ProcessStep key={step.title} step={step} index={i} total={PROCESS.length} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
